import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  deleteCuratedArticle,
  getCuratedArticles,
  getCuratedArticlesPayload,
  isCuratedArticlesApiSuccess,
} from '../../services/curatedArticlesApi';
import StatusBadge from './components/StatusBadge';
import {
  getArticleCoverTitle,
  getArticleDeckSummary,
  getArticleDiscoverSectionId,
  getArticleHotTake,
  getArticleSlideCount,
  getArticleUpdatedAt,
} from './utils/articleFieldHelpers';

const STATUS_OPTIONS = [
  { value: '', label: 'All (working list)' },
  { value: 'draft', label: 'Draft' },
  { value: 'scraping', label: 'Scraping' },
  { value: 'generating', label: 'Generating' },
  { value: 'review', label: 'Review' },
  { value: 'published', label: 'Published' },
  { value: 'failed', label: 'Failed' },
  { value: 'archived', label: 'Deleted' },
];

const DELETE_CONFIRM =
  'Delete this article? It will be removed from the list and Pulse after unpublish/archive.';

function ArticleList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [articles, setArticles] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const applied = useMemo(
    () => ({
      status: searchParams.get('status') || '',
    }),
    [searchParams]
  );
  const [status, setStatus] = useState(applied.status);

  useEffect(() => {
    setStatus(applied.status);
  }, [applied]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSessionExpired = () => {
    showSnackbar('Session expired. Please login again.', 'error');
    setTimeout(() => {
      window.location = '/login';
    }, 2000);
  };

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getCuratedArticles(applied.status ? { status: applied.status } : {});
      if (res.statusCode === '10003') {
        handleSessionExpired();
        return;
      }
      if (!isCuratedArticlesApiSuccess(res)) {
        setError(res.message || 'Failed to load articles.');
        setArticles([]);
        return;
      }
      const payload = getCuratedArticlesPayload(res);
      setArticles(Array.isArray(payload) ? payload : payload?.articles || []);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load articles.');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const applyFilters = () => {
    const params = {};
    if (status) params.status = status;
    setSearchParams(params);
  };

  const handleDelete = async (event, id) => {
    event.preventDefault();
    event.stopPropagation();
    if (!window.confirm(DELETE_CONFIRM)) return;
    const res = await deleteCuratedArticle(id);
    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar('Article archived');
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } else {
      showSnackbar(res.message || 'Failed to delete', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Articles</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadArticles} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            component={Link}
            to="/admin/curated-articles/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Create article
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Same mental model as <Link to="/admin/pulse">Pulse cards</Link>: title, summary, cover, hot
        take, then publish. Published articles sync to <strong>Pulse</strong> only — not Discover /
        Digout Digest. No publish cap. Default list excludes deleted (archived).
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s.value || 'all'} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={applyFilters}>
          Apply
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Hot take</TableCell>
              <TableCell>Slides</TableCell>
              <TableCell>Sort</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {applied.status === 'archived' ? 'No deleted articles.' : 'No articles found.'}
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => {
                const title = getArticleCoverTitle(article) || `Article #${article.id}`;
                const summary = getArticleDeckSummary(article);
                const hotTake = getArticleHotTake(article);
                const sectionId = getArticleDiscoverSectionId(article);
                return (
                  <TableRow
                    key={article.id}
                    hover
                    component={Link}
                    to={`/admin/curated-articles/${article.id}`}
                    sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                  >
                    <TableCell>
                      <StatusBadge status={article.status} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={
                          article.status === 'archived'
                            ? { textDecoration: 'line-through', opacity: 0.7 }
                            : undefined
                        }
                      >
                        {title}
                      </Typography>
                      {summary && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {summary.length > 120 ? `${summary.slice(0, 120).trim()}` : summary}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        {hotTake
                          ? hotTake.length > 80
                            ? `${hotTake.slice(0, 77)}…`
                            : hotTake
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getArticleSlideCount(article)}</TableCell>
                    <TableCell>{article.sortOrder ?? article.sort_order ?? '—'}</TableCell>
                    <TableCell>
                      {sectionId != null ? (
                        <Typography variant="caption" color="text.secondary">
                          {sectionId}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {getArticleUpdatedAt(article) ? formatDate(getArticleUpdatedAt(article)) : '—'}
                    </TableCell>
                    <TableCell>
                      {article.status !== 'archived' && (
                        <IconButton
                          size="small"
                          color="error"
                          title="Delete"
                          onClick={(e) => handleDelete(e, article.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {articles.length} article{articles.length === 1 ? '' : 's'}
      </Typography>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ArticleList;
